import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackendService } from '../../core/backend.service';
import { CONFIG } from '../../core/config';
import { PermissionsService } from '../../core/permissions.service';
import { HbNotification } from '../../notifications/notification';
import { NotificationLife } from '../../notifications/notification-life';
import { NotificationState } from '../../notifications/notification-state';
import { NotificationService } from '../../notifications/notification.service';
import { UserPermissionKeys } from '../../security/authorization/user-permission-keys';
import { ItemScenarios } from '../../shared/enums/item-scenarios';
import { HierarchyTemplate } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template';
import { HierarchyTemplateGroup } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template-group';
import { HierarchyTemplateTab } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template-tab';
import { RangeDeleteData } from '../../viewmodels/admin/template-attributes-delete-data';
import { HierarchyTemplateAttributesTableService } from './hierarchy-template-attributes-table.service';

@Injectable({
  providedIn: 'root',
})
export class HierarchyTemplateDetailsService {
  public hierarchyTemplate$ = new BehaviorSubject<HierarchyTemplate>(null);
  private userPermissionKeys: UserPermissionKeys = new UserPermissionKeys();
  private hasEditPermission: boolean = false;
  private hasCreatePermission: boolean = false;
  private hasDeletePermission: boolean = false;
  public isAddVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isDeleteVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public allowDelete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public scenario: ItemScenarios = ItemScenarios.New;
  public hierarchyTemplateGroupSelection$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public handleSaveHierachyTemplate$: BehaviorSubject<HierarchyTemplate> = new BehaviorSubject<HierarchyTemplate>(null);
  public processSavedHierachyTemplate$: BehaviorSubject<HierarchyTemplate> = new BehaviorSubject<HierarchyTemplate>(null);

  public constructor(
    private backendService: BackendService,
    private tableService: HierarchyTemplateAttributesTableService,
    private permissionsService: PermissionsService,
    private notificationService: NotificationService,
  ) {
    this.subscribeToPermissionsChanges();
  }

  private subscribeToPermissionsChanges(): void {
    this.permissionsService.userPermissionKeyValuePairs.subscribe(() => {
      this.setAccessVariables();
      this.changeActionButtonsState();
      this.assignPermissions();
    });
  }

  private assignPermissions(): void {
    this.hasEditPermission = this.permissionsService.checkUserHasPermissions(this.userPermissionKeys.adminHierarchyTemplateUpdate);
  }

  private setAccessVariables(): void {
    const { adminInspectionTemplateCreate, adminInspectionTemplateDelete } = this.permissionsService.userPermissionKeys;

    this.hasCreatePermission = this.permissionsService.checkUserHasPermissions(adminInspectionTemplateCreate);
    this.isAddVisible$.next(this.hasCreatePermission);
    this.hasDeletePermission = this.permissionsService.checkUserHasPermissions(adminInspectionTemplateDelete);
  }

  public loadHierarchyTemplate(hierarchyTemplateId: string): Observable<HierarchyTemplate> {
    this.scenario = ItemScenarios.Existing;

    return this.backendService.getById<HierarchyTemplate>(HierarchyTemplate, hierarchyTemplateId).pipe(
      map(hierarchyTemplate => {
        if (hierarchyTemplate) {
          this.hierarchyTemplate$.next(hierarchyTemplate);
          this.changeActionButtonsState();

          return hierarchyTemplate;
        }
      }),
    );
  }

  public loadAttributes(templateId: string, hierarchyTemplateGroupId: string): void {
    this.tableService.templateId = templateId;
    this.tableService.hierarchyTemplateGroupId = hierarchyTemplateGroupId;
    this.tableService.setParams(this.hasEditPermission);
    this.tableService.loadAttributeCollection();
  }

  public createNewTemplateAttributeData(): void {
    this.hierarchyTemplate$.next(new HierarchyTemplate());
    this.tableService.totalRecords = 0;
  }

  public deleteHierarchyTemplate(templateId: string): Observable<HierarchyTemplate> {
    return this.backendService.deleteFromSpecifiedRoute<HierarchyTemplate>(
      'Hierarchy Template Attribute',
      CONFIG.apiRoutes.hierarchyTemplateWithAttribute,
      templateId,
    );
  }

  public clearTemplateValues(): void {
    this.hierarchyTemplate$.next(null);
    this.processSavedHierachyTemplate$.next(null);
    this.handleSaveHierachyTemplate$.next(null);
    this.tableService.clear();
  }

  private allowDelete(): boolean {
    const template = this.hierarchyTemplate$.value;

    return template && this.scenario === ItemScenarios.Existing && this.hasDeletePermission;
  }

  private changeActionButtonsState(): void {
    this.allowDelete$.next(this.allowDelete());
    this.isDeleteVisible$.next(this.hasDeletePermission);
  }

  public loadHierarchyTemplateTabsAndGroups(hierarchyTemplateId: string): Observable<HierarchyTemplateTab[]> {
    return this.backendService.getAllFromRoute<HierarchyTemplateTab>(HierarchyTemplateTab, new HierarchyTemplateTab().apiRoute, hierarchyTemplateId);
  }

  public createDefaultTab(tabName?: string): HierarchyTemplateTab {
    const hierarchyTemplateTab = new HierarchyTemplateTab();

    hierarchyTemplateTab.name = tabName ? tabName : 'General';
    hierarchyTemplateTab.sortingIndex = 0;

    hierarchyTemplateTab.hierarchyTemplateGroups = [this.createDefaultGroup()];

    return hierarchyTemplateTab;
  }

  public createDefaultGroup(grpName?: string): HierarchyTemplateGroup {
    const hierarchyTemplateGrp = new HierarchyTemplateGroup();

    hierarchyTemplateGrp.name = grpName ? grpName : 'General';

    return hierarchyTemplateGrp;
  }

  public getSaveOrUpdateMethod(templateDetails: HierarchyTemplate): Observable<any> {
    if (this.scenario === ItemScenarios.New || templateDetails.hierarchyTemplateId === ItemScenarios.Copy) {
      if (templateDetails.hierarchyTemplateId === ItemScenarios.Copy) {
        templateDetails.hierarchyTemplateTabs.forEach(tab => {
          tab.hierarchyTemplateGroups.forEach(group => {
            group.attributes.forEach(attribute => {
              delete attribute.hierarchyTemplateAttributeId;
              delete attribute.hierarchyTemplateGroupId;
              delete attribute.hierarchyTemplateId;
              delete attribute.uomId;

            });
            delete group.hierarchyTemplateTabId;
            delete group.hierarchyTemplateGroupId;
          });
          delete tab.hierarchyTemplateTabId;
          delete tab.hierarchyTemplateId;
        });
        templateDetails.hierarchyTemplateId = this.tableService.templateId;
      } else {
        templateDetails.hierarchyTemplateId = undefined;
      }

      // create mode
      return this.backendService.postObjectRoute(CONFIG.apiRoutes.hierarchyTemplateSave, templateDetails);
    } else {
      // update mode
      return this.backendService.putObjectRoute(
        HierarchyTemplate,
        CONFIG.apiRoutes.hierarchyTemplateUpdate,
        templateDetails.hierarchyTemplateId,
        templateDetails,
      );
    }
  }

  public processSavedHierarchyTemplate(hierarchyTemplate: HierarchyTemplate): void {
    this.processSavedHierachyTemplate$.next(hierarchyTemplate);
  }

  private showNotification(description: string, state: NotificationState): void {
    const notification = new HbNotification();

    notification.description = description;
    notification.state = state;
    notification.life = NotificationLife.Short;

    this.notificationService.displayToast(notification);
  }

  public showFailedNotification(description: string): void {
    this.showNotification(description, NotificationState.Failed);
  }

  public showCompletedNotification(description: string): void {
    this.showNotification(description, NotificationState.Complete);
  }

  public deleteHierarchyTemplateTab(tabId: string): Observable<HierarchyTemplateTab> {
    return this.backendService.deleteFromSpecifiedRoute<HierarchyTemplateTab>(
      'Hierarchy Template Attribute',
      CONFIG.apiRoutes.hierarchyTemplateTab,
      tabId,
    );
  }

  public deleteHierarchyTemplateGroup(groupId: string): Observable<boolean> {
    const deleteData = new RangeDeleteData(this.tableService.templateId, [groupId]);

    return this.backendService.deleteRangeObject<HierarchyTemplateGroup>(HierarchyTemplateGroup, deleteData);
  }
}
